import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaGithub, FaLinkedin, FaPaperPlane } from 'react-icons/fa';
import './Contact.css';

const Contact = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    // Reset form
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
  };

  const contactInfo = [
    {
      icon: FaEnvelope,
      title: 'Email',
      value: 'srk161265@gmail.com',
      link: 'mailto:srk161265@gmail.com'
    },
    {
      icon: FaPhone,
      title: 'Phone',
      value: '+92 311-0775738',
      link: 'tel:+923110775738'
    },
    {
      icon: FaMapMarkerAlt,
      title: 'Location',
      value: 'Rawalpindi, Pakistan',
      link: '#'
    }
  ];

  const socialLinks = [
    {
      icon: FaGithub,
      name: 'GitHub',
      url: 'https://github.com/ShahzaibRK',
      color: '#333'
    },
    {
      icon: FaLinkedin,
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/in/shahzaib-khan-7515522ba',
      color: '#0077b5'
    },
    {
      icon: FaEnvelope,
      name: 'Email',
      url: 'mailto:srk161265@gmail.com',
      color: '#ea4335'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="contact" className="contact" ref={ref}>
      <div className="container">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.h2 className="section-title" variants={itemVariants}>
            Get In Touch
          </motion.h2>

          <motion.p className="contact-subtitle" variants={itemVariants}>
            Ready to discuss cybersecurity opportunities or collaborate on projects? 
            Let's connect and explore how we can work together.
          </motion.p>

          <div className="contact-content">
            <motion.div className="contact-info" variants={itemVariants}>
              <h3>Contact Information</h3>
              
              <div className="contact-details">
                {contactInfo.map((info, index) => (
                  <motion.a
                    key={info.title}
                    href={info.link}
                    className="contact-item"
                    initial={{ opacity: 0, x: -20 }}
                    animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    whileHover={{ scale: 1.05, x: 10 }}
                  >
                    <div className="contact-icon">
                      <info.icon />
                    </div>
                    <div className="contact-text">
                      <h4>{info.title}</h4>
                      <p>{info.value}</p>
                    </div>
                  </motion.a>
                ))}
              </div>

              <div className="social-section">
                <h4>Follow Me</h4>
                <div className="social-links">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={social.name}
                      href={social.url}
                      target={social.url.startsWith('mailto:') ? '_self' : '_blank'}
                      rel={social.url.startsWith('mailto:') ? '' : 'noopener noreferrer'}
                      className="social-link"
                      style={{ '--social-color': social.color }}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
                      transition={{ delay: 0.8 + index * 0.1 }}
                      whileHover={{ 
                        scale: 1.2, 
                        rotate: 5,
                        backgroundColor: social.color 
                      }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <social.icon />
                    </motion.a>
                  ))}
                </div>
              </div>

              <motion.div 
                className="availability-status"
                variants={itemVariants}
              >
                <div className="status-indicator">
                  <div className="status-dot"></div>
                  <span>Available for opportunities</span>
                </div>
                <p>Open to cybersecurity roles, remote roles, and collaborative projects</p>
              </motion.div>
            </motion.div>

            <motion.div className="contact-form-container" variants={itemVariants}>
              <h3>Send Message</h3>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <motion.div 
                    className="form-group"
                    whileFocus={{ scale: 1.02 }}
                  >
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </motion.div>
                  <motion.div 
                    className="form-group"
                    whileFocus={{ scale: 1.02 }}
                  >
                    <input
                      type="email"
                      name="email"
                      placeholder="Your Email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </motion.div>
                </div>

                <motion.div 
                  className="form-group"
                  whileFocus={{ scale: 1.02 }}
                >
                  <input
                    type="text"
                    name="subject"
                    placeholder="Subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                  />
                </motion.div>

                <motion.div 
                  className="form-group"
                  whileFocus={{ scale: 1.02 }}
                >
                  <textarea
                    name="message"
                    placeholder="Your Message"
                    rows="6"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                </motion.div>

                <motion.button
                  type="submit"
                  className="btn btn-primary submit-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaPaperPlane />
                  Send Message
                </motion.button>
              </form>
            </motion.div>
          </div>
        </motion.div>
      </div>

      <motion.footer className="footer" variants={itemVariants}>
        <div className="container">
          <div className="footer-content">
            <p>&copy; Shahzaib Khan. All rights reserved.</p>
            <p>Cybersecurity Analyst</p>
          </div>
        </div>
      </motion.footer>
    </section>
  );
};

export default Contact;